import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { gungGetPropertyOfObject } from '../../utils/gung-utils';
import { BaseInputDirective } from '../base-input/base-input.directive';
import { OptionsList } from '../gung-select-option-input/gung-select-option-input.component';

export interface OptionsListMulti extends OptionsList {
  selected: boolean;
  hide?: boolean;
  _badgeValue?: string;
  _imageValue?: string;
}

@Component({
  selector: 'lib-gung-input-multi-select',
  templateUrl: './gung-input-multi-select.component.html',
  styleUrls: ['./gung-input-multi-select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GungInputMultiSelectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class GungInputMultiSelectComponent extends BaseInputDirective<string[]> implements OnInit, OnChanges {
  @Input()
  public label: string;

  @Input()
  public showId = true;

  @Input()
  previewEmpty = 'Choose available options';

  @Input()
  public tooltip?: string;
  @Input('optionsList') set setOptionList(value: OptionsListMulti[]) {
    this.optionsList = JSON.parse(JSON.stringify(value));
    this.ngOnInit();
  }
  @Input()
  public previewField?: string | string[];

  @Input()
  public optionBadgeField?: string | string[];

  @Input()
  public optionImageField?: string | string[];

  @Input()
  public dropdownCustomClass: string;

  @Input()
  public optionsList: OptionsListMulti[];
  optionsFilterList: OptionsListMulti[];

  @Output()
  selectedValues = new EventEmitter<string[]>();

  @Output()
  selectedValuesClose = new EventEmitter<string[]>();
  changedBeforeClose = false;

  dropdownIsOpen = false;
  searchString: string;
  currentSelectedValues: string[];

  previewList: string[];

  @Input()
  singleSelection: boolean;

  @Input()
  disabled: boolean;

  @ViewChild('dropDownEle')
  public dropDownEle: NgbDropdown;

  @Input()
  focusInput?: boolean;

  @Input()
  showEmptyIds?: boolean;

  @ViewChild('filterListSearch') filterListSearch: any;

  @Input()
  ngBootstrapContainer: 'body' | null = null;

  @Input()
  suppressSearch = false;

  @Input()
  enableSelectAll = false;

  @Input()
  enableLazyLoad = false;

  @Input()
  lazyLoadLimit = 25;

  showAll = false;
  visibleOptions: OptionsListMulti[] = [];
  hasMoreOptions = false;

  private resolveField(field: string | string[] | undefined, option: OptionsListMulti): string {
    if (!field) return '';
    if (Array.isArray(field)) {
      return field
        .map(f => (['', ' ', ',', ', ', ' - ', '-'].includes(f) ? f : gungGetPropertyOfObject(f, option)))
        .join('');
    }
    return gungGetPropertyOfObject(field, option) || '';
  }

  private resolveOptionFields(list: OptionsListMulti[]): OptionsListMulti[] {
    return list.map(o => ({
      ...o,
      _badgeValue: this.resolveField(this.optionBadgeField, o),
      _imageValue: this.resolveField(this.optionImageField, o)
    }));
  }

  private updateVisibleOptions(): void {
    if (!this.enableLazyLoad || this.showAll || this.searchString) {
      this.visibleOptions = this.resolveOptionFields(this.optionsFilterList);
      this.hasMoreOptions = false;
      return;
    }
    const selected = this.optionsFilterList.filter(o => o.selected);
    const unselected = this.optionsFilterList.filter(o => !o.selected);
    this.hasMoreOptions = unselected.length > this.lazyLoadLimit;
    this.visibleOptions = this.resolveOptionFields([...selected, ...unselected.slice(0, this.lazyLoadLimit)]);
  }

  onShowAll(): void {
    this.showAll = true;
    this.updateVisibleOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.optionsFilterList = this.searchString
      ? this.filterBySearchTerm(this.searchString, this.optionsList)
      : this.optionsList;

    this.currentSelectedValues = this.optionsList.filter(o => o.selected).map(o => o.id);
    this.updateVisibleOptions();
    this.updatePreview();
  }

  ngOnInit(): void {
    this.optionsFilterList = this.searchString
      ? this.filterBySearchTerm(this.searchString, this.optionsList)
      : this.optionsList;

    this.currentSelectedValues = this.optionsList.filter(o => o.selected).map(o => o.id);
    this.updateVisibleOptions();
    this.updatePreview();
  }

  private updatePreview() {
    this.previewList = this.optionsList
      .filter(o => this.currentSelectedValues.includes(o.id))
      .map(o => {
        if (this.previewField) {
          if (Array.isArray(this.previewField)) {
            const opts = this.previewField.map(field => {
              if (['', ' ', ',', ', ', ' - ', '-'].includes(field)) {
                return field;
              }
              return gungGetPropertyOfObject(field, o);
            });

            if (this.showEmptyIds) {
              const newOpts: string[] = [];
              for (let index = 0; index < opts.length; index++) {
                const previousOpt = opts[index - 1];
                const opt = opts[index];
                if (!(['', ' ', ',', ', ', ' - ', '-'].includes(opt) && !previousOpt)) {
                  newOpts.push(opt);
                }
              }
              return newOpts.join('');
            }

            return opts.join('');
          }
          return o[this.previewField];
        }
        return o.id;
      });
  }

  setSearch(search: string): void {
    this.searchString = search;
    this.optionsFilterList = (!search || search.trim() === '')
      ? this.optionsList
      : this.filterBySearchTerm(search, this.optionsList);
    this.updateVisibleOptions();
  }

  filterBySearchTerm(searchTerm: string, items: OptionsList[]): any[] {
    return items.filter(item => {
      let hasHitAllTerms = true;
      const queryTerms = searchTerm.split(' ');
      const terms = [item.id, item.name];
      if (this.previewField) {
        let previewValue = undefined;
        if (Array.isArray(this.previewField)) {
          previewValue = this.previewField
            .map(field => {
              if (['', ' ', ',', ', ', ' - ', '-'].includes(field)) {
                return field;
              }
              return gungGetPropertyOfObject(field, item);
            })
            .join('');
        } else {
          previewValue = item[this.previewField];
        }
        terms.push(previewValue);
      }
      queryTerms.forEach(queryTerm => {
        const locatedTerm = terms.find(term => {
          if (term === null || term === undefined) {
            return false;
          }
          return term.toUpperCase().indexOf(queryTerm.toUpperCase()) >= 0;
        });

        hasHitAllTerms = hasHitAllTerms && !!locatedTerm;
      });

      return hasHitAllTerms;
    });
  }

  get selectableFilteredOptions(): OptionsListMulti[] {
    return (this.optionsFilterList || []).filter(o => !o.hide && (!!o.id || this.showEmptyIds));
  }

  get allFilteredSelected(): boolean {
    const selectable = this.selectableFilteredOptions;
    return selectable.length > 0 && selectable.every(o => this.currentSelectedValues.includes(o.id));
  }

  toggleSelectAll(): void {
    const selectAll = !this.allFilteredSelected;
    const ids = new Set(this.selectableFilteredOptions.map(o => o.id));
    if (selectAll) {
      ids.forEach(id => {
        if (!this.currentSelectedValues.includes(id)) {
          this.currentSelectedValues.push(id);
        }
      });
    } else {
      this.currentSelectedValues = this.currentSelectedValues.filter(id => !ids.has(id));
    }
    this.optionsList.forEach(o => (o.selected = this.currentSelectedValues.includes(o.id)));
    this.updateVisibleOptions();
    this.updatePreview();
    this.selectedValues.emit(this.currentSelectedValues);
    this.changedBeforeClose = true;
  }

  setSelectValue(valueId: string, selected: boolean) {
    if (this.singleSelection) {
      this.currentSelectedValues = [valueId];
      this.optionsList.forEach(o => (o.selected = o.id === valueId));
      this.searchString = '';
      this.filterListSearch?.reset();
      this.setSearch('');
      this.dropDownEle.close();
    } else if (!selected) {
      this.currentSelectedValues = this.currentSelectedValues.filter(id => id !== valueId);
    } else {
      if (!this.currentSelectedValues.includes(valueId)) {
        this.currentSelectedValues.push(valueId);
      }
    }
    this.updatePreview();
    this.selectedValues.emit(this.currentSelectedValues);
    this.changedBeforeClose = true;
  }

  openChange(event: boolean) {
    this.dropdownIsOpen = event;
    if (!event && this.changedBeforeClose) {
      this.selectedValuesClose.emit(this.currentSelectedValues);
      this.changedBeforeClose = false;
    }
  }

  getPreviewField(option): string {
    if (this.previewField) {
      if (Array.isArray(this.previewField)) {
        const opts = this.previewField.map(field => {
          if (['', ' ', ',', ', ', ' - ', '-'].includes(field)) {
            return field;
          }
          return gungGetPropertyOfObject(field, option);
        });

        if (this.showEmptyIds) {
          const newOpts: string[] = [];
          for (let index = 0; index < opts.length; index++) {
            const previousOpt = opts[index - 1];
            const opt = opts[index];
            if (!(['', ' ', ',', ', ', ' - ', '-'].includes(opt) && !previousOpt)) {
              newOpts.push(opt);
            }
          }
          return newOpts.join('');
        }

        return opts.join('');
      }
      return option[this.previewField];
    }
    return option.id;
  }

}
